import { BasedOn } from '@wix/ambassador-bookings-v2-bookings-settings/types';
import { ActionFactoryParams } from '../../../../utils/ControlledComponent/ControlledComponent.types';
import { CalendarContext } from '../../../../utils/context/contextFactory';
import {
  getEndOfMonthAsLocalDateTime,
  getLocalTimezone,
  getStartOfMonthAsLocalDateTime,
  getTodayLocalDateTimeStartOfDay,
} from '../../../../utils/dateAndTime/dateAndTime';
import { CalendarStatus } from '../../ViewModel/widgetViewModel/widgetViewModel';
import { SetSelectedMonth } from '../setSelectedMonth/setSelectedMonth';
import { CalendarState } from '../../controller';
import { SetSelectedDate } from '../setSelectedDate/setSelectedDate';
import { TriggeredByOptions } from '../../../../types/types';
import { SetSelectedRange } from '../setSelectedRange/setSelectedRange';
import { getLocalDateTimeRangeForDay } from '../../../../utils/getLocalDateTimeRangeForDay/getLocalDateTimeRangeForDay';
import { AddError } from '../addError/addError';
import { InitializeCalendarDateOptions } from '../../../../utils/bi/consts';
import { bookingsCalendarPageLoaded } from '@wix/bi-logger-wixboost-ugc/v2';
import {
  isDailyAgendaWeeklyPickerLayout,
  isDailyTimeSlotsWeeklyPickerLayout,
  isWeeklyTimeSlotsLayout,
  isWeeklyTimetableLayout,
} from '../../../../utils/layouts';
import { AutoSelectTime } from '../autoSelectTime/autoSelectTime';
import {
  getUrlQueryParamValue,
  BookingsQueryParams as BookingsQueryParamsUtils,
  AUTO_OPEN_PREFERENCES_MODAL_STORAGE_KEY,
} from '@wix/bookings-catalog-calendar-viewer-utils';
import { isCalendarPage } from '../../../../utils/presets';
import { OpenServicesPreferencesModal } from '../openServicesPreferencesModal/openServicesPreferencesModal';

export type InitializeWidget = () => Promise<void>;

export function createInitializeWidgetAction(
  {
    getControllerState,
    context,
  }: ActionFactoryParams<CalendarState, CalendarContext>,
  setSelectedDate: SetSelectedDate,
  setSelectedMonth: SetSelectedMonth,
  setSelectedRange: SetSelectedRange,
  addError: AddError,
  autoSelectTime: AutoSelectTime,
  openServicesPreferencesModal: OpenServicesPreferencesModal,
): InitializeWidget {
  return async () => {
    const { flowAPI, biLogger, settings, settingsParams, languageSettings } =
      context;
    const {
      environment: { isSSR },
    } = flowAPI;

    if (isSSR) {
      return;
    }

    const [state, setState] = getControllerState();
    autoOpenPreferencesModalIfNeeded({
      state,
      context,
      openServicesPreferencesModal,
    });

    const selectedTimezone = getSelectedTimezone(context);
    setState({ selectedTimezone });

    const initialLocalDate = await getInitializeCalendarDate({
      state,
      context,
      addError,
    });

    if (
      isWeeklyTimeSlotsLayout(settings, settingsParams) ||
      isWeeklyTimetableLayout(settings, settingsParams)
    ) {
      const range = getLocalDateTimeRangeForDay(
        languageSettings!.dateRegionalSettingsLocale!,
        initialLocalDate,
      );
      await setSelectedRange(range, TriggeredByOptions.INITIALIZE_WIDGET);
    } else if (
      isDailyTimeSlotsWeeklyPickerLayout(settings, settingsParams) ||
      isDailyAgendaWeeklyPickerLayout(settings, settingsParams)
    ) {
      const range = getLocalDateTimeRangeForDay(
        languageSettings!.dateRegionalSettingsLocale!,
        initialLocalDate,
      );
      setSelectedRange(range, TriggeredByOptions.INITIALIZE_WIDGET);
      await setSelectedDate(
        initialLocalDate,
        TriggeredByOptions.INITIALIZE_WIDGET,
      );
    } else {
      const startOfMonthAsLocalDateTime =
        getStartOfMonthAsLocalDateTime(initialLocalDate);
      setSelectedMonth(
        startOfMonthAsLocalDateTime,
        TriggeredByOptions.INITIALIZE_WIDGET,
      );
      await setSelectedDate(
        initialLocalDate,
        TriggeredByOptions.INITIALIZE_WIDGET,
      );
    }

    autoSelectTime(TriggeredByOptions.INITIALIZE_WIDGET);

    setState({ calendarStatus: CalendarStatus.IDLE });

    biLogger.report(bookingsCalendarPageLoaded({}));
  };
}

function getSelectedTimezone({ bookingsSettings, flowAPI }: CalendarContext) {
  const localTimezone = getLocalTimezone();
  const preSelectedTimezone = getUrlQueryParamValue(
    flowAPI.controllerConfig.wixCodeApi,
    BookingsQueryParamsUtils.TIMEZONE,
  );
  const businessTimeZone = bookingsSettings.siteProperties?.timeZone;

  const isPreselectedTimezoneValid = [businessTimeZone, localTimezone].includes(
    preSelectedTimezone,
  );

  const defaultTimezone =
    bookingsSettings.displayTimeZone?.basedOn === BasedOn.CUSTOMER_TIME_ZONE
      ? localTimezone
      : businessTimeZone ?? undefined;

  return isPreselectedTimezoneValid ? preSelectedTimezone : defaultTimezone;
}

const getInitializeCalendarDate = async ({
  state,
  context,
  addError,
}: {
  state: CalendarState;
  context: CalendarContext;
  addError: AddError;
}): Promise<string> => {
  const { selectedDate, selectedTimezone } = state;
  const { settings, settingsParams, calendarApi } = context;
  const isAnonymousCancellationFlow =
    getUrlQueryParamValue(
      context.flowAPI.controllerConfig.wixCodeApi,
      BookingsQueryParamsUtils.REFERRAL,
    ) === 'batel';

  const todayLocalDateTime = getTodayLocalDateTimeStartOfDay(selectedTimezone!);

  if (isAnonymousCancellationFlow) {
    return selectedDate || todayLocalDateTime;
  }
  if (selectedDate) {
    return selectedDate;
  }

  const shouldInitializeCalendarOnToday =
    settings.get(settingsParams.initializeCalendarDate) ===
    InitializeCalendarDateOptions.TODAY;
  if (shouldInitializeCalendarOnToday) {
    return todayLocalDateTime;
  } else {
    const sixMonthsFromNow = getEndOfMonthAsLocalDateTime(
      todayLocalDateTime,
      7,
    );
    const nextAvailableDate = await calendarApi.getNextAvailableDate(
      {
        fromAsLocalDateTime: todayLocalDateTime,
        toAsLocalDateTime: sixMonthsFromNow,
      },
      { state, context, onError: addError },
    );
    return nextAvailableDate || todayLocalDateTime;
  }
};

const autoOpenPreferencesModalIfNeeded = ({
  state,
  context,
  openServicesPreferencesModal,
}: {
  state: CalendarState;
  context: CalendarContext;
  openServicesPreferencesModal: OpenServicesPreferencesModal;
}) => {
  const { isPreferencesModalPartOfTheFlow } = state;
  const {
    preset,
    calendarSelections,
    flowAPI: { experiments },
  } = context;

  if (!experiments.enabled('specs.bookings.removeSkipPreferencesModalParam')) {
    return autoOpenPreferencesModalIfNeededDeprecated({
      state,
      context,
      openServicesPreferencesModal,
    });
  }

  const shouldAutoOpenPreferencesModal =
    isCalendarPage(preset) &&
    isPreferencesModalPartOfTheFlow &&
    !calendarSelections;

  if (shouldAutoOpenPreferencesModal) {
    openServicesPreferencesModal();
  }
};

/**
 * @deprecated Will be removed after specs.bookings.removeSkipPreferencesModalParam reaches 100%
 */
const autoOpenPreferencesModalIfNeededDeprecated = ({
  state,
  context,
  openServicesPreferencesModal,
}: {
  state: CalendarState;
  context: CalendarContext;
  openServicesPreferencesModal: OpenServicesPreferencesModal;
}) => {
  const { isPreferencesModalPartOfTheFlow } = state;
  const {
    preset,
    isDateAndTimeViewMode,
    flowAPI: { controllerConfig },
  } = context;

  const shouldSkipAutoOpenPreferencesModal =
    getUrlQueryParamValue(
      controllerConfig.wixCodeApi,
      BookingsQueryParamsUtils.SKIP_PREFERENCES_MODAL,
    ) === 'true';

  const shouldAutoOpenPreferencesModal =
    isCalendarPage(preset) &&
    isPreferencesModalPartOfTheFlow &&
    !shouldSkipAutoOpenPreferencesModal;

  if (shouldAutoOpenPreferencesModal) {
    openServicesPreferencesModal();
  } else if (
    isDateAndTimeViewMode &&
    controllerConfig.platformAPIs.storage.session.getItem(
      AUTO_OPEN_PREFERENCES_MODAL_STORAGE_KEY,
    ) === 'true'
  ) {
    controllerConfig.platformAPIs.storage.session.removeItem(
      AUTO_OPEN_PREFERENCES_MODAL_STORAGE_KEY,
    );
    openServicesPreferencesModal();
  }
};
